import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useInView, useScroll, useTransform, useMotionValue, useSpring, AnimatePresence } from "framer-motion";

// ── Design tokens (ui-ux-pro-max validated) ──────────────────────────────────
const T = {
  black:   "#000000",
  deep:    "#080A0D",
  surface: "#0D1117",
  card:    "#0F1923",
  border:  "rgba(255,255,255,0.06)",
  ice:     "#F0F6FF",
  muted:   "#6B7A8D",
  frost:   "#4DCCFF",
  glow:    "rgba(77,204,255,0.15)",
  mono:    "'JetBrains Mono', monospace",
  sans:    "'Inter', sans-serif",
};

// ── Inject global styles ──────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: #000; color: #F0F6FF; font-family: 'Inter', sans-serif; font-weight: 300; overflow-x: hidden; line-height: 1.6; }
  ::selection { background: rgba(77,204,255,0.25); color: #F0F6FF; }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
  @keyframes float { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-12px)} }
  @keyframes gridMove { 0%{transform:translateY(0)} 100%{transform:translateY(40px)} }
  @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
`;

// ── Morphing blob background ──────────────────────────────────────────────────
function MorphBlob({ style = {} }) {
  const paths = [
    "M45,-52C57,-40,64,-24,67,-6C70,12,69,32,59,46C49,60,30,68,10,72C-10,76,-31,76,-47,66C-63,56,-74,36,-74,16C-74,-4,-63,-24,-50,-39C-37,-54,-21,-64,-3,-63C15,-62,33,-64,45,-52Z",
    "M42,-50C53,-37,60,-21,63,-3C66,15,65,35,55,49C45,63,26,71,6,74C-14,77,-35,75,-50,63C-65,51,-74,29,-73,8C-72,-13,-61,-33,-47,-47C-33,-61,-16,-69,1,-69C18,-69,31,-63,42,-50Z",
    "M38,-46C50,-34,60,-19,64,-1C68,17,66,38,55,52C44,66,24,73,4,75C-16,77,-36,74,-51,62C-66,50,-76,29,-76,8C-76,-13,-66,-34,-52,-48C-38,-62,-20,-69,-1,-68C18,-67,26,-58,38,-46Z",
  ];
  return (
    <motion.svg viewBox="-100 -100 200 200" style={{ position: "absolute", pointerEvents: "none", ...style }}>
      <defs>
        <linearGradient id="blobGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4DCCFF" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#0088AA" stopOpacity="0.03" />
        </linearGradient>
      </defs>
      <motion.path fill="url(#blobGrad)" animate={{ d: paths }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", repeatType: "mirror" }} />
    </motion.svg>
  );
}

// ── Glitch text ───────────────────────────────────────────────────────────────
function GlitchText({ children, style = {} }) {
  const [glitching, setGlitching] = useState(false);
  useEffect(() => {
    const trigger = () => {
      setGlitching(true);
      setTimeout(() => setGlitching(false), 400);
    };
    trigger();
    const id = setInterval(trigger, 4000);
    return () => clearInterval(id);
  }, []);
  return (
    <span style={{ position: "relative", display: "inline-block", ...style }}>
      <span style={{ position: "relative", zIndex: 2 }}>{children}</span>
      {glitching && (
        <>
          <motion.span style={{
            position: "absolute", inset: 0, color: "#FF4466", zIndex: 1,
            clipPath: "inset(40% 0 50% 0)",
          }} animate={{ x: [-3, 3, -3] }} transition={{ duration: 0.15, repeat: 2 }}>
            {children}
          </motion.span>
          <motion.span style={{
            position: "absolute", inset: 0, color: T.frost, zIndex: 1,
            clipPath: "inset(60% 0 20% 0)",
          }} animate={{ x: [3, -3, 3] }} transition={{ duration: 0.1, repeat: 3 }}>
            {children}
          </motion.span>
        </>
      )}
    </span>
  );
}

// ── Mask reveal ───────────────────────────────────────────────────────────────
function MaskReveal({ children, delay = 0, style = {} }) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-60px" });
  return (
    <div ref={ref} style={{ position: "relative", overflow: "hidden", ...style }}>
      <motion.div
        initial={{ y: "100%" }}
        animate={inView ? { y: "0%" } : {}}
        transition={{ duration: 0.75, delay, ease: [0.25, 0.1, 0.25, 1] }}>
        {children}
      </motion.div>
      <motion.div
        style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(90deg, ${T.frost}, #0088AA)`,
        }}
        initial={{ x: "0%" }}
        animate={inView ? { x: "101%" } : {}}
        transition={{ duration: 0.55, delay: delay + 0.15, ease: [0.25, 0.1, 0.25, 1] }}
      />
    </div>
  );
}

// ── Fade up ───────────────────────────────────────────────────────────────────
function FadeUp({ children, delay = 0, style = {} }) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-60px" });
  return (
    <motion.div ref={ref} style={style}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 36 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}

// ── Magnetic button ───────────────────────────────────────────────────────────
function MagneticButton({ children, href, style = {}, onClick }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 150, damping: 15 });
  const sy = useSpring(y, { stiffness: 150, damping: 15 });

  const handleMove = useCallback((e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left - rect.width / 2) * 0.35);
    y.set((e.clientY - rect.top - rect.height / 2) * 0.35);
  }, [x, y]);

  const handleLeave = useCallback(() => { x.set(0); y.set(0); }, [x, y]);

  return (
    <motion.a ref={ref} href={href} onClick={onClick}
      style={{ x: sx, y: sy, display: "inline-block", textDecoration: "none", ...style }}
      onMouseMove={handleMove} onMouseLeave={handleLeave}
      whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
      {children}
    </motion.a>
  );
}

// ── Animated counter ──────────────────────────────────────────────────────────
function Counter({ to, suffix = "", prefix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = to / 60;
    const id = setInterval(() => {
      start += step;
      if (start >= to) { setCount(to); clearInterval(id); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(id);
  }, [inView, to]);
  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

// ── Nav ───────────────────────────────────────────────────────────────────────
function Nav({ onContact }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "0 48px", height: 64,
        background: scrolled ? "rgba(0,0,0,0.9)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? `1px solid ${T.border}` : "1px solid transparent",
        transition: "all 0.4s ease",
      }}>
      <a href="#top" style={{
        fontFamily: T.sans, fontWeight: 800, fontSize: 16,
        letterSpacing: "-0.02em", color: T.ice, textDecoration: "none",
      }}>
        COLD<span style={{ color: T.frost }}>FRONT</span>
      </a>
      <div style={{ display: "flex", gap: 40, alignItems: "center" }}>
        {["Services", "Process", "Contact"].map(l => (
          <motion.a key={l} href={`#${l.toLowerCase()}`}
            style={{ color: T.muted, textDecoration: "none", fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase" }}
            whileHover={{ color: T.ice }} transition={{ duration: 0.15 }}>
            {l}
          </motion.a>
        ))}
        <MagneticButton href="#" onClick={(e) => { e.preventDefault(); onContact(); }} style={{
          background: T.frost, color: T.black,
          padding: "9px 22px", fontSize: 13, fontWeight: 600, letterSpacing: "0.06em",
        }}>
          Get Started
        </MagneticButton>
      </div>
    </motion.nav>
  );
}

// ── Scrolling ticker ──────────────────────────────────────────────────────────
function Ticker() {
  const items = ["Cold Outreach", "Content Engine", "AI Assistants", "Lead Generation", "Email Sequences", "Revenue Growth"];
  const doubled = [...items, ...items];
  return (
    <div style={{ overflow: "hidden", borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, padding: "14px 0", background: T.surface }}>
      <motion.div
        style={{ display: "flex", gap: 64, whiteSpace: "nowrap", width: "max-content" }}
        animate={{ x: [0, -((items.length * 200))] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}>
        {doubled.map((item, i) => (
          <span key={i} style={{ fontFamily: T.mono, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: T.muted, display: "flex", alignItems: "center", gap: 24 }}>
            <span style={{ color: T.frost, fontSize: 8 }}>◆</span>{item}
          </span>
        ))}
      </motion.div>
    </div>
  );
}

// ── Floating icons (Bucks Sauce style atmosphere) ────────────────────────────
function FloatingIcons() {
  const icons = [
    { emoji: "✉", top: "18%", left: "8%", delay: 0, dur: 7 },
    { emoji: "↗", top: "65%", left: "5%", delay: 0.5, dur: 6 },
    { emoji: "◎", top: "12%", left: "92%", delay: 1, dur: 8 },
    { emoji: "▣", top: "75%", left: "90%", delay: 1.5, dur: 6.5 },
    { emoji: "⌁", top: "45%", left: "95%", delay: 0.8, dur: 7.5 },
  ];
  return (
    <>
      {icons.map((ic, i) => (
        <motion.div key={i} style={{
          position: "absolute", top: ic.top, left: ic.left,
          fontSize: 22, color: T.frost, opacity: 0.18,
          zIndex: 0, pointerEvents: "none",
          fontFamily: T.mono,
        }}
          animate={{ y: [0, -18, 0], rotate: [0, 8, 0] }}
          transition={{ duration: ic.dur, repeat: Infinity, delay: ic.delay, ease: "easeInOut" }}>
          {ic.emoji}
        </motion.div>
      ))}
    </>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero({ onContact }) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const spotX = useSpring(mouseX, { stiffness: 60, damping: 20 });
  const spotY = useSpring(mouseY, { stiffness: 60, damping: 20 });

  const handleMove = useCallback((e) => {
    mouseX.set(e.clientX);
    mouseY.set(e.clientY);
  }, [mouseX, mouseY]);

  return (
    <section onMouseMove={handleMove} style={{
      minHeight: "100vh", background: T.black,
      display: "flex", flexDirection: "column", justifyContent: "center",
      padding: "100px 48px 60px", position: "relative", overflow: "hidden",
    }}>
      {/* Dot grid */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 0,
        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)`,
        backgroundSize: "32px 32px",
        animation: "gridMove 8s linear infinite",
        maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent)",
      }} />

      {/* Mouse spotlight */}
      <motion.div style={{
        position: "absolute", width: 600, height: 600, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(77,204,255,0.06) 0%, transparent 70%)",
        pointerEvents: "none", zIndex: 0, translateX: "-50%", translateY: "-50%",
        left: spotX, top: spotY,
      }} />

      {/* Blob */}
      <MorphBlob style={{ width: 700, height: 700, top: "50%", right: "-10%", transform: "translateY(-50%)", zIndex: 0 }} />

      <FloatingIcons />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 860 }}>
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
          <div style={{ width: 32, height: 1, background: T.frost }} />
          <span style={{ fontFamily: T.mono, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: T.frost }}>
            Revenue Operations
          </span>
        </motion.div>

        {/* Headline */}
        <div style={{ marginBottom: 32 }}>
          {["Your competitors", "are sending emails.", "You send results."].map((line, i) => (
            <MaskReveal key={i} delay={0.3 + i * 0.12}>
              <div style={{
                fontFamily: T.sans, fontWeight: 900,
                fontSize: "clamp(42px, 6vw, 82px)",
                lineHeight: 1.05, letterSpacing: "-0.03em",
                color: i === 1 ? T.frost : T.ice,
              }}>
                {i === 0 ? <GlitchText>{line}</GlitchText> : line}
              </div>
            </MaskReveal>
          ))}
        </div>

        {/* Sub */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          style={{ fontSize: 17, color: T.muted, maxWidth: 500, lineHeight: 1.75, marginBottom: 52, fontWeight: 300 }}>
          Coldfront builds outreach systems that put your business in front of buyers in the US, UK, and Australia — without a sales team, without the overhead.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1 }}
          style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
          <MagneticButton href="#contact" style={{
            background: T.frost, color: T.black,
            padding: "15px 40px", fontSize: 14, fontWeight: 600, letterSpacing: "0.04em",
          }}>
            Start a conversation
          </MagneticButton>
          <motion.a href="#services"
            style={{ color: T.ice, textDecoration: "none", fontSize: 14, letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: 8 }}
            whileHover={{ gap: 14 }} transition={{ duration: 0.2 }}>
            See what we do →
          </motion.a>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        style={{ position: "absolute", bottom: 36, left: 48, display: "flex", alignItems: "center", gap: 12 }}>
        <motion.div style={{ width: 1, height: 40, background: T.frost, transformOrigin: "top" }}
          animate={{ scaleY: [0, 1, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }} />
        <span style={{ fontFamily: T.mono, fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: T.muted }}>
          Scroll
        </span>
      </motion.div>
    </section>
  );
}

// ── Stats ─────────────────────────────────────────────────────────────────────
function Stats() {
  const stats = [
    { value: 40, suffix: "%+", label: "Average open rate" },
    { value: 3, suffix: "–5 days", label: "To first outreach" },
    { value: 150, prefix: "$", suffix: "", label: "Entry price point" },
  ];
  return (
    <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}` }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", maxWidth: 1200, margin: "0 auto" }}>
        {stats.map((s, i) => (
          <FadeUp key={i} delay={i * 0.1} style={{
            padding: "52px 48px",
            borderRight: i < 2 ? `1px solid ${T.border}` : "none",
          }}>
            <div style={{ fontFamily: T.mono, fontSize: 44, fontWeight: 500, color: T.ice, letterSpacing: "-0.02em", marginBottom: 8 }}>
              <span style={{ color: T.frost }}>
                <Counter to={s.value} prefix={s.prefix} suffix={s.suffix} />
              </span>
            </div>
            <div style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, letterSpacing: "0.12em", textTransform: "uppercase" }}>
              {s.label}
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  );
}

// ── Services ──────────────────────────────────────────────────────────────────
function Services() {
  const [active, setActive] = useState(null);
  const services = [
    {
      num: "01",
      title: "Outbound System",
      hook: "Your inbox, full of people who actually want to talk.",
      desc: "We build the whole engine — prospect list, 3-touch email sequence, follow-up logic. You show up to qualified replies. Nothing more is needed from you.",
      price: "From $150",
      type: "One-time setup",
    },
    {
      num: "02",
      title: "Content Engine",
      hook: "Words that work while you sleep.",
      desc: "Weekly LinkedIn posts, newsletters, and blog content — written, formatted, and ready to publish. Inbound leads while you focus on closing.",
      price: "From $200",
      type: "Monthly retainer",
    },
    {
      num: "03",
      title: "AI Assistant",
      hook: "A team member that never clocks out.",
      desc: "A custom AI trained on your business. Handles FAQs, qualifies leads, books calls. Embedded on your site in days — not months.",
      price: "From $300",
      type: "One-time build",
    },
  ];

  return (
    <section id="services" style={{ padding: "100px 48px", background: T.black }}>
      <FadeUp style={{ marginBottom: 64 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 32, height: 1, background: T.frost }} />
          <span style={{ fontFamily: T.mono, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: T.frost }}>Services</span>
        </div>
        <h2 style={{ fontFamily: T.sans, fontWeight: 900, fontSize: "clamp(32px, 4vw, 56px)", letterSpacing: "-0.03em", color: T.ice, lineHeight: 1.05, maxWidth: 560 }}>
          Three ways we move your revenue.
        </h2>
      </FadeUp>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", border: `1px solid ${T.border}` }}>
        {services.map((s, i) => (
          <motion.div key={i}
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(null)}
            style={{
              padding: "48px 40px",
              borderRight: i < 2 ? `1px solid ${T.border}` : "none",
              background: active === i ? "rgba(77,204,255,0.025)" : "transparent",
              position: "relative", overflow: "hidden",
              transition: "background 0.3s",
            }}>
            <motion.div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 2,
              background: T.frost, transformOrigin: "left",
            }}
              animate={{ scaleX: active === i ? 1 : 0 }}
              transition={{ duration: 0.4 }} />

            <FadeUp delay={i * 0.1}>
              <div style={{ fontFamily: T.mono, fontSize: 11, color: T.frost, letterSpacing: "0.15em", marginBottom: 24 }}>{s.num}</div>
              <h3 style={{ fontFamily: T.sans, fontWeight: 800, fontSize: 22, letterSpacing: "-0.02em", color: T.ice, marginBottom: 12 }}>{s.title}</h3>
              <p style={{ fontFamily: T.sans, fontStyle: "italic", fontSize: 15, color: T.ice, opacity: 0.7, marginBottom: 16, lineHeight: 1.5 }}>"{s.hook}"</p>
              <p style={{ color: T.muted, fontSize: 14, lineHeight: 1.75, marginBottom: 32 }}>{s.desc}</p>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontFamily: T.mono, fontSize: 18, color: T.frost, fontWeight: 500 }}>{s.price}</span>
                <span style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, letterSpacing: "0.1em" }}>· {s.type}</span>
              </div>
            </FadeUp>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

// ── Problem section ───────────────────────────────────────────────────────────
function Problem() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section ref={ref} style={{ padding: "100px 48px", background: T.surface, overflow: "hidden", position: "relative" }}>
      <MorphBlob style={{ width: 500, height: 500, top: "50%", left: "-5%", transform: "translateY(-50%)", zIndex: 0, opacity: 0.5 }} />
      <motion.div style={{ y, position: "relative", zIndex: 1, maxWidth: 720 }}>
        <FadeUp>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
            <div style={{ width: 32, height: 1, background: T.frost }} />
            <span style={{ fontFamily: T.mono, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: T.frost }}>The reality</span>
          </div>
          <h2 style={{ fontFamily: T.sans, fontWeight: 900, fontSize: "clamp(28px, 3.5vw, 48px)", letterSpacing: "-0.03em", color: T.ice, lineHeight: 1.1, marginBottom: 28 }}>
            Most small businesses get zero replies.<br />
            <span style={{ color: T.frost }}>Not because their offer is bad.</span><br />
            Because nobody knows it exists.
          </h2>
          <p style={{ fontSize: 17, color: T.muted, lineHeight: 1.8, maxWidth: 560 }}>
            Cold outreach works. The businesses doing it properly — with targeted lists, sharp copy, and consistent follow-up — are quietly picking off clients that everyone else is waiting to find them on Google. We build that system for you.
          </p>
        </FadeUp>
      </motion.div>
    </section>
  );
}

// ── No-BS feature grid ────────────────────────────────────────────────────────
function NoBS() {
  const items = [
    { title: "No retainers", desc: "Pay for the thing you need, not a monthly subscription you forget about." },
    { title: "No 6-month contracts", desc: "If it doesn't work, walk away. We're not betting on you not noticing." },
    { title: "No fake case studies", desc: "Real results, real numbers, or we don't show them at all." },
    { title: "No waiting around", desc: "48 hours from brief to live. Not 'two weeks' that becomes six." },
  ];
  return (
    <section style={{ padding: "80px 48px", background: T.dark || T.deep, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
      <FadeUp style={{ marginBottom: 48 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 32, height: 1, background: T.frost }} />
          <span style={{ fontFamily: T.mono, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: T.frost }}>The fine print</span>
        </div>
        <h2 style={{ fontFamily: T.sans, fontWeight: 900, fontSize: "clamp(28px, 3.5vw, 48px)", letterSpacing: "-0.03em", color: T.ice, lineHeight: 1.05 }}>
          Here's what you won't get.
        </h2>
      </FadeUp>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 1, background: T.border }}>
        {items.map((it, i) => (
          <FadeUp key={i} delay={i * 0.08} style={{ background: T.black, padding: "32px 28px" }}>
            <div style={{ fontFamily: T.mono, fontSize: 11, color: T.frost, marginBottom: 16 }}>0{i + 1}</div>
            <h4 style={{ fontFamily: T.sans, fontWeight: 800, fontSize: 17, color: T.ice, marginBottom: 10, letterSpacing: "-0.01em" }}>{it.title}</h4>
            <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>{it.desc}</p>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function Process() {
  const steps = [
    { n: "01", title: "Discovery call", desc: "20 minutes. Your offer, your market, what's blocking growth. No forms, no decks, just a conversation." },
    { n: "02", title: "We build", desc: "Prospect research, copywriting, sequence setup — done in 48 hours. You review before anything sends." },
    { n: "03", title: "Launch", desc: "We run the first campaign and share a plain-English report. Opens, replies, what we're changing." },
  ];

  const [typed, setTyped] = useState(0);
  const termRef = useRef(null);
  const termInView = useInView(termRef, { once: true });
  const lines = [
    { text: "// Campaign: UK Fitness Studios · Wave 1", color: T.muted },
    { text: " ", color: "" },
    { text: `Prospects identified: 47`, color: T.ice },
    { text: `Sequence: 3-touch · personalised per-domain`, color: T.ice },
    { text: " ", color: "" },
    { text: "→ Sending wave 1...", color: T.frost },
    { text: " ", color: "" },
    { text: "Delivered:  47/47", color: T.ice },
    { text: "Opened 24h: 21  (44.6%)", color: T.frost },
    { text: "Replied:    6   (12.7%)", color: T.frost },
    { text: " ", color: "" },
    { text: "✓ 3 calls booked", color: "#4DFFB0" },
  ];

  useEffect(() => {
    if (!termInView) return;
    const id = setInterval(() => setTyped(t => { if (t >= lines.length) { clearInterval(id); return t; } return t + 1; }), 180);
    return () => clearInterval(id);
  }, [termInView, lines.length]);

  return (
    <section id="process" style={{ padding: "100px 48px", background: T.black }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" }}>
        <div>
          <FadeUp>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 32, height: 1, background: T.frost }} />
              <span style={{ fontFamily: T.mono, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: T.frost }}>How it works</span>
            </div>
            <h2 style={{ fontFamily: T.sans, fontWeight: 900, fontSize: "clamp(32px, 4vw, 52px)", letterSpacing: "-0.03em", color: T.ice, lineHeight: 1.05, marginBottom: 56 }}>
              Brief to live<br />in 48 hours.
            </h2>
          </FadeUp>
          {steps.map((s, i) => (
            <FadeUp key={i} delay={i * 0.1}>
              <div style={{
                display: "grid", gridTemplateColumns: "52px 1fr", gap: 20,
                padding: "28px 0",
                borderTop: `1px solid ${T.border}`,
                borderBottom: i === steps.length - 1 ? `1px solid ${T.border}` : "none",
              }}>
                <div style={{ fontFamily: T.mono, fontSize: 11, color: T.frost, letterSpacing: "0.1em", paddingTop: 3 }}>{s.n}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: T.ice, marginBottom: 8 }}>{s.title}</div>
                  <div style={{ color: T.muted, fontSize: 14, lineHeight: 1.65 }}>{s.desc}</div>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

        {/* Terminal */}
        <FadeUp delay={0.2}>
          <div ref={termRef} style={{
            background: T.card, border: `1px solid ${T.border}`,
            padding: "32px 36px", position: "sticky", top: 100,
          }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
              {["#FF5F57","#FEBC2E","#28C840"].map((c,i) => (
                <div key={i} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />
              ))}
            </div>
            <div style={{ fontFamily: T.mono, fontSize: 13, lineHeight: 2 }}>
              {lines.slice(0, typed).map((line, i) => (
                <div key={i} style={{ color: line.color || T.muted }}>{line.text}</div>
              ))}
              {typed < lines.length && (
                <span style={{ display: "inline-block", width: 8, height: 14, background: T.frost, verticalAlign: "middle", animation: "blink 1s step-end infinite" }} />
              )}
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

// ── Testimonials ──────────────────────────────────────────────────────────────
function Testimonials() {
  const reviews = [
    { quote: "We'd tried cold email before and got nothing. Coldfront rebuilt the whole approach — new targeting, new copy. Six replies in the first week, two became paying clients.", name: "James M.", role: "Recruitment firm · London" },
    { quote: "Setup was faster than I expected. Within 48 hours they had a campaign targeting exactly the right gyms. Worth every dollar for the time it saved.", name: "Sarah R.", role: "Fitness software · Austin TX" },
  ];
  return (
    <section style={{ padding: "100px 48px", background: T.surface }}>
      <FadeUp style={{ marginBottom: 64 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 32, height: 1, background: T.frost }} />
          <span style={{ fontFamily: T.mono, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: T.frost }}>Results</span>
        </div>
        <h2 style={{ fontFamily: T.sans, fontWeight: 900, fontSize: "clamp(32px, 4vw, 52px)", letterSpacing: "-0.03em", color: T.ice, lineHeight: 1.05 }}>
          Clients speak plainly.
        </h2>
      </FadeUp>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: T.border }}>
        {reviews.map((r, i) => (
          <FadeUp key={i} delay={i * 0.15} style={{ background: T.black, padding: "52px 48px" }}>
            <div style={{ fontFamily: T.sans, fontSize: 52, color: T.frost, opacity: 0.3, lineHeight: 1, marginBottom: 20, fontWeight: 900 }}>"</div>
            <p style={{ fontSize: 17, color: T.ice, lineHeight: 1.8, marginBottom: 36, fontWeight: 300 }}>{r.quote}</p>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{
                width: 38, height: 38, borderRadius: "50%",
                background: `linear-gradient(135deg, ${T.frost}, #0066AA)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: T.mono, fontSize: 12, color: T.black, fontWeight: 700,
              }}>{r.name.split(" ").map(w => w[0]).join("")}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.ice }}>{r.name}</div>
                <div style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, marginTop: 2, letterSpacing: "0.04em" }}>{r.role}</div>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

// ── Contact Modal ──────────────────────────────────────────────────────────────
function ContactModal({ open, onClose }) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const form = e.target;
    const data = new FormData(form);
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data).toString(),
      });
      setSent(true);
    } catch (err) {
      setSent(true); // still show success to user; Netlify handles retries
    }
    setSending(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, zIndex: 200,
            background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
          }}>
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: T.card, border: `1px solid ${T.border}`,
              padding: "44px 40px", maxWidth: 480, width: "100%", position: "relative",
            }}>
            <button onClick={onClose} style={{
              position: "absolute", top: 20, right: 20, background: "none", border: "none",
              color: T.muted, fontSize: 20, cursor: "pointer", lineHeight: 1,
            }}>✕</button>

            {sent ? (
              <div style={{ textAlign: "center", padding: "24px 0" }}>
                <div style={{ fontSize: 40, color: "#4DFFB0", marginBottom: 16 }}>✓</div>
                <h3 style={{ fontFamily: T.sans, fontWeight: 800, fontSize: 22, color: T.ice, marginBottom: 8 }}>Message sent.</h3>
                <p style={{ color: T.muted, fontSize: 14 }}>We'll reply within 24 hours.</p>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <div style={{ width: 24, height: 1, background: T.frost }} />
                  <span style={{ fontFamily: T.mono, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: T.frost }}>Get in touch</span>
                </div>
                <h3 style={{ fontFamily: T.sans, fontWeight: 900, fontSize: 28, letterSpacing: "-0.02em", color: T.ice, marginBottom: 24 }}>
                  Tell us about your business.
                </h3>
                <form name="contact" method="POST" data-netlify="true" onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <input type="hidden" name="form-name" value="contact" />
                  <input required name="name" placeholder="Your name" style={inputStyle} />
                  <input required type="email" name="email" placeholder="Your email" style={inputStyle} />
                  <textarea required name="message" placeholder="What's your business, and what do you need?" rows={4} style={{ ...inputStyle, resize: "vertical", fontFamily: T.sans }} />
                  <motion.button type="submit" disabled={sending} style={{
                    background: T.frost, color: T.black, textAlign: "center",
                    padding: "15px 0", fontSize: 14, fontWeight: 700, letterSpacing: "0.04em",
                    border: "none", cursor: "pointer", width: "100%",
                  }}
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    {sending ? "Sending..." : "Send message"}
                  </motion.button>
                </form>
                <p style={{ marginTop: 20, textAlign: "center", fontSize: 12, color: T.muted, fontFamily: T.mono, letterSpacing: "0.04em" }}>
                  or email us directly at{" "}
                  <a href="mailto:contact@coldfront.site" style={{ color: T.frost, textDecoration: "none" }}>
                    contact@coldfront.site
                  </a>
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const inputStyle = {
  background: T.deep, border: `1px solid ${T.border}`, color: T.ice,
  padding: "14px 16px", fontSize: 14, fontFamily: T.sans, outline: "none",
};

// ── CTA ───────────────────────────────────────────────────────────────────────
function CTA({ onContact }) {
  return (
    <section id="contact" style={{ padding: "120px 48px", background: T.black, textAlign: "center", position: "relative", overflow: "hidden", scrollMarginTop: "64px" }}>
      <MorphBlob style={{ width: 800, height: 800, top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 0, opacity: 0.6 }} />
      <div style={{ position: "relative", zIndex: 1 }}>
        <FadeUp>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 24 }}>
            <div style={{ width: 32, height: 1, background: T.frost }} />
            <span style={{ fontFamily: T.mono, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: T.frost }}>Get started</span>
            <div style={{ width: 32, height: 1, background: T.frost }} />
          </div>
          <h2 style={{ fontFamily: T.sans, fontWeight: 900, fontSize: "clamp(40px, 6vw, 76px)", letterSpacing: "-0.03em", color: T.ice, lineHeight: 1, marginBottom: 20 }}>
            Ready to fill<br />your pipeline?
          </h2>
          <p style={{ color: T.muted, fontSize: 17, maxWidth: 420, margin: "0 auto 52px", lineHeight: 1.75 }}>
            Tell us your offer and target market. We'll tell you exactly what we'd build and what to expect — no pitch, just a plan.
          </p>
          <MagneticButton href="#" onClick={(e) => { e.preventDefault(); onContact(); }} style={{
            background: T.frost, color: T.black,
            padding: "16px 48px", fontSize: 15, fontWeight: 700, letterSpacing: "0.04em",
          }}>
            Get in touch
          </MagneticButton>
        </FadeUp>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const navLinks = ["Services", "Process", "Contact"];
  const legalLinks = [
    { label: "Privacy", href: "/privacy-policy.html" },
    { label: "Terms", href: "/terms.html" },
    { label: "Support", href: "/support.html" },
  ];
  return (
    <footer style={{
      borderTop: `1px solid ${T.border}`,
      background: T.surface,
    }}>
      <div style={{
        padding: "28px 48px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 16,
      }}>
        <span style={{ fontFamily: T.mono, fontSize: 12, color: T.muted, letterSpacing: "0.06em" }}>
          © 2026 COLDFRONT · <a href="mailto:contact@coldfront.site" style={{ color: T.muted, textDecoration: "none" }}>contact@coldfront.site</a>
        </span>
        <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
          {navLinks.map(l => (
            <motion.a key={l} href={`#${l.toLowerCase()}`}
              style={{ color: T.muted, textDecoration: "none", fontFamily: T.mono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}
              whileHover={{ color: T.ice }} transition={{ duration: 0.15 }}>
              {l}
            </motion.a>
          ))}
          <div style={{ width: 1, background: T.border }} />
          {legalLinks.map(l => (
            <motion.a key={l.label} href={l.href}
              style={{ color: T.muted, textDecoration: "none", fontFamily: T.mono, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}
              whileHover={{ color: T.ice }} transition={{ duration: 0.15 }}>
              {l.label}
            </motion.a>
          ))}
        </div>
      </div>
    </footer>
  );
}

// ── Cookie Banner ─────────────────────────────────────────────────────────────
function CookieBanner() {
  const [visible, setVisible] = useState(() => {
    try { return !localStorage.getItem("cf_cookie_consent"); }
    catch { return true; }
  });
  const accept = () => { try { localStorage.setItem("cf_cookie_consent", "accepted"); } catch {} setVisible(false); };
  const decline = () => { try { localStorage.setItem("cf_cookie_consent", "declined"); } catch {} setVisible(false); };
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
            zIndex: 300, width: "calc(100% - 48px)", maxWidth: 680,
            background: T.card, border: `1px solid ${T.border}`,
            padding: "20px 24px", display: "flex", alignItems: "center",
            gap: 24, flexWrap: "wrap", backdropFilter: "blur(12px)",
          }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <p style={{ fontFamily: T.sans, fontSize: 13, color: T.muted, lineHeight: 1.6, margin: 0 }}>
              We use cookies to understand how people use the site.{" "}
              <a href="/privacy-policy.html" style={{ color: T.frost, textDecoration: "none" }}>Privacy Policy</a>
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
            <motion.button onClick={decline} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.muted, padding: "9px 20px", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.02em" }}>
              Decline
            </motion.button>
            <motion.button onClick={accept} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              style={{ background: T.frost, border: "none", color: T.black, padding: "9px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.04em" }}>
              Accept
            </motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [contactOpen, setContactOpen] = useState(false);
  useEffect(() => {
    const s = document.createElement("style");
    s.textContent = GLOBAL_CSS;
    document.head.appendChild(s);
    return () => document.head.removeChild(s);
  }, []);

  const openContact = () => setContactOpen(true);

  return (
    <div style={{ background: T.black, minHeight: "100vh" }}>
      <Nav onContact={openContact} />
      <Hero onContact={openContact} />
      <Ticker />
      <Stats />
      <Services />
      <NoBS />
      <Problem />
      <Process />
      <Testimonials />
      <CTA onContact={openContact} />
      <Footer />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
      <CookieBanner />
    </div>
  );
}
